import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  Eye,
  MoreVertical,
  Paperclip,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Users2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { MediaPickerDialog } from '@/components/common/media-picker-dialog'
import { WhatsAppPreview } from '@/components/common/whatsapp-preview'
import { useAuth } from '@/hooks/use-auth'
import { useChannels } from '@/hooks/use-channels'
import {
  type ScheduledGroupMessage,
  useCancelGroupMessage,
  useChannelGroups,
  useCreateGroupMessage,
  useDeleteGroupMessage,
  useGroupMessages,
  useSyncChannelGroups,
  useUpdateGroupMessage,
} from '@/hooks/use-group-messages'
import type { MediaAsset } from '@/types/media-assets'

export const Route = createFileRoute('/_app/grupos/')({
  component: GruposPage,
})

const STATUS: Record<
  ScheduledGroupMessage['status'],
  { label: string; className: string }
> = {
  SCHEDULED: {
    label: 'Agendada',
    className: 'border-warning/40 bg-warning/10 text-warning',
  },
  SENDING: {
    label: 'Enviando',
    className: 'border-info/40 bg-info/10 text-info',
  },
  SENT: {
    label: 'Enviada',
    className: 'border-success/40 bg-success/10 text-success',
  },
  FAILED: {
    label: 'Falhou',
    className: 'border-destructive/40 bg-destructive/10 text-destructive',
  },
  CANCELED: { label: 'Cancelada', className: 'bg-muted text-muted-foreground' },
}

const EDITABLE_STATUSES: ScheduledGroupMessage['status'][] = [
  'SCHEDULED',
  'FAILED',
  'CANCELED',
]

const TARGET_STATUS: Record<
  'PENDING' | 'SENT' | 'FAILED',
  { label: string; className: string }
> = {
  PENDING: { label: 'Pendente', className: 'bg-muted text-muted-foreground' },
  SENT: {
    label: 'Enviado',
    className: 'border-success/40 bg-success/10 text-success',
  },
  FAILED: {
    label: 'Falhou',
    className: 'border-destructive/40 bg-destructive/10 text-destructive',
  },
}

const MEDIA_ACCEPT =
  'image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv'

function pad(n: number) {
  return String(n).padStart(2, '0')
}

function toLocalInputValue(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function GruposPage() {
  const { currentOrgId, currentRole } = useAuth()
  const isAdmin = currentRole === 'OWNER' || currentRole === 'ADMIN'
  const { data: messages = [], isLoading } = useGroupMessages(
    isAdmin ? currentOrgId : null
  )
  const { data: channels = [] } = useChannels(isAdmin ? currentOrgId : null)
  const cancel = useCancelGroupMessage(currentOrgId)
  const remove = useDeleteGroupMessage(currentOrgId)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<ScheduledGroupMessage | null>(null)
  const [viewing, setViewing] = useState<ScheduledGroupMessage | null>(null)
  const [deleteTarget, setDeleteTarget] =
    useState<ScheduledGroupMessage | null>(null)

  const channelName = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of channels) map.set(c.id, c.name)
    return map
  }, [channels])

  if (!isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6">
        <div className="text-center">
          <Users2 className="text-muted-foreground mx-auto h-10 w-10" />
          <p className="mt-3 font-medium">Acesso restrito</p>
          <p className="text-muted-foreground text-sm">
            Apenas administradores podem gerenciar mensagens de grupos.
          </p>
        </div>
      </div>
    )
  }

  const dialogOpen = formOpen || !!editing
  function closeDialog() {
    setFormOpen(false)
    setEditing(null)
  }

  return (
    <div className="bg-muted/40 flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={Users2}
        title="Grupos"
        description="Agende mensagens para grupos de WhatsApp."
        actions={
          <>
            <Button type="button" onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4" />
              Agendar mensagem
            </Button>
          </>
        }
      />

      <main className="min-h-0 flex-1 overflow-auto p-6">
        {isLoading ? (
          <Skeleton className="h-64 w-full rounded-lg" />
        ) : (
          <div className="bg-card overflow-hidden rounded-lg border shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40 hover:bg-muted/40">
                  <TableHead>Mensagem</TableHead>
                  <TableHead>Canal</TableHead>
                  <TableHead>Grupos</TableHead>
                  <TableHead>Agendada para</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-28 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => {
                  const status = STATUS[message.status]
                  const sentCount = message.targets.filter(
                    (t) => t.status === 'SENT'
                  ).length
                  return (
                    <TableRow key={message.id} className="hover:bg-muted/30">
                      <TableCell className="max-w-xs">
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full">
                            <Users2 className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">
                              {message.body || message.mediaFileName || 'Mídia'}
                            </p>
                            {message.mediaFileName ? (
                              <p className="text-muted-foreground truncate text-xs">
                                <Paperclip className="mr-1 inline h-3 w-3" />
                                {message.mediaFileName}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {channelName.get(message.channelId) ?? 'Canal'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {message.targets.length} grupo
                        {message.targets.length === 1 ? '' : 's'}
                        {message.status !== 'SCHEDULED' &&
                        message.status !== 'CANCELED'
                          ? ` · ${sentCount} enviadas`
                          : ''}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDateTime(message.scheduledAt)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={status.className}>
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Ações"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => setViewing(message)}
                              >
                                <Eye className="h-4 w-4" />
                                Visualizar
                              </DropdownMenuItem>
                              {EDITABLE_STATUSES.includes(message.status) ? (
                                <DropdownMenuItem
                                  onClick={() => setEditing(message)}
                                >
                                  <Pencil className="h-4 w-4" />
                                  Editar
                                </DropdownMenuItem>
                              ) : null}
                              {message.status === 'SCHEDULED' ? (
                                <DropdownMenuItem
                                  disabled={cancel.isPending}
                                  onClick={() => {
                                    void cancel
                                      .mutateAsync(message.id)
                                      .then(() =>
                                        toast.success('Agendamento cancelado')
                                      )
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                  Cancelar
                                </DropdownMenuItem>
                              ) : null}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteTarget(message)}
                              >
                                <Trash2 className="h-4 w-4" />
                                Excluir
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {messages.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground h-32 text-center"
                    >
                      Nenhuma mensagem agendada. Clique em “Agendar mensagem”.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </div>
        )}
      </main>

      <ScheduleDialog
        open={dialogOpen}
        onClose={closeDialog}
        orgId={currentOrgId}
        editing={editing}
      />

      <MessageViewDialog
        message={viewing}
        channelName={viewing ? channelName.get(viewing.channelId) : undefined}
        onClose={() => setViewing(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir agendamento?"
        description="Esta ação é permanente e não pode ser desfeita."
        confirmLabel="Excluir"
        destructive
        onConfirm={() => {
          if (!deleteTarget) return
          const id = deleteTarget.id
          setDeleteTarget(null)
          void remove.mutateAsync(id).then(() => toast.success('Removido'))
        }}
      />
    </div>
  )
}

function ScheduleDialog({
  open,
  onClose,
  orgId,
  editing,
}: {
  open: boolean
  onClose: () => void
  orgId: string | null
  editing: ScheduledGroupMessage | null
}) {
  const { data: channels = [] } = useChannels(orgId)
  const evolutionChannels = channels.filter(
    (c) => c.provider === 'EVOLUTION' && c.status === 'CONNECTED'
  )
  const [channelId, setChannelId] = useState('')
  const groupsQuery = useChannelGroups(orgId, channelId || null)
  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data])
  const syncGroups = useSyncChannelGroups(orgId)

  const [groupSearch, setGroupSearch] = useState('')
  const filteredGroups = useMemo(() => {
    const query = groupSearch.trim().toLowerCase()
    if (!query) return groups
    return groups.filter((g) => g.subject.toLowerCase().includes(query))
  }, [groups, groupSearch])

  const [selected, setSelected] = useState<Record<string, string>>({})
  const [body, setBody] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [media, setMedia] = useState<{
    mediaUrl: string
    mediaStorageKey: string
    mediaMimeType: string
    mediaFileName: string
  } | null>(null)

  const create = useCreateGroupMessage(orgId)
  const update = useUpdateGroupMessage(orgId)

  // Pré-preenche ao abrir para edição (ou limpa para criação).
  useEffect(() => {
    if (!open) return
    setGroupSearch('')
    if (editing) {
      setChannelId(editing.channelId)
      setSelected(
        Object.fromEntries(
          editing.targets.map((t) => [t.groupJid, t.groupSubject ?? t.groupJid])
        )
      )
      setBody(editing.body ?? '')
      // FAILED/CANCELED podem ter data no passado; nesse caso propõe um futuro próximo.
      const existingAt = new Date(editing.scheduledAt)
      setScheduledAt(
        toLocalInputValue(
          existingAt.getTime() > Date.now()
            ? existingAt
            : new Date(Date.now() + 5 * 60_000)
        )
      )
      setMedia(
        editing.mediaUrl
          ? {
              mediaUrl: editing.mediaUrl,
              mediaStorageKey: editing.mediaStorageKey ?? '',
              mediaMimeType: editing.mediaMimeType ?? '',
              mediaFileName: editing.mediaFileName ?? '',
            }
          : null
      )
    } else {
      setChannelId('')
      setSelected({})
      setBody('')
      setScheduledAt('')
      setMedia(null)
    }
  }, [open, editing])

  function toggleGroup(jid: string, subject: string) {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[jid]) delete next[jid]
      else next[jid] = subject
      return next
    })
  }

  async function onSyncGroups() {
    if (!channelId) return
    try {
      await syncGroups.mutateAsync(channelId)
      toast.success('Grupos atualizados')
    } catch {
      toast.error('Falha ao atualizar os grupos')
    }
  }

  function selectMediaAsset(asset: MediaAsset) {
    setMedia({
      mediaUrl: asset.publicUrl,
      mediaStorageKey: asset.storageKey,
      mediaMimeType: asset.mimeType,
      mediaFileName: asset.fileName,
    })
    toast.success('Mídia selecionada')
  }

  async function submit() {
    const groupsPayload = Object.entries(selected).map(([jid, subject]) => ({
      jid,
      subject,
    }))
    if (!channelId) return toast.error('Selecione um canal')
    if (groupsPayload.length === 0)
      return toast.error('Selecione ao menos um grupo')
    if (!body.trim() && !media)
      return toast.error('Escreva a mensagem ou anexe uma mídia')
    if (!scheduledAt) return toast.error('Escolha a data e hora')
    const scheduledIso = new Date(scheduledAt).toISOString()
    if (new Date(scheduledIso).getTime() <= Date.now())
      return toast.error('A data/hora deve ser no futuro')

    const payload = {
      channelId,
      groups: groupsPayload,
      body: body.trim() || undefined,
      scheduledAt: scheduledIso,
      ...(media
        ? {
            mediaUrl: media.mediaUrl,
            mediaStorageKey: media.mediaStorageKey || undefined,
            mediaMimeType: media.mediaMimeType || undefined,
            mediaFileName: media.mediaFileName || undefined,
          }
        : {}),
    }

    if (editing) {
      await update.mutateAsync({ id: editing.id, payload })
      toast.success('Agendamento atualizado')
    } else {
      await create.mutateAsync(payload)
      toast.success('Mensagem agendada')
    }
    onClose()
  }

  const saving = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Editar agendamento' : 'Agendar mensagem em grupos'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Canal</Label>
            <Select
              value={channelId || undefined}
              onValueChange={(value) => {
                setChannelId(value)
                setSelected({})
                setGroupSearch('')
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione o canal (QR Code)" />
              </SelectTrigger>
              <SelectContent>
                {evolutionChannels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {evolutionChannels.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                Nenhum canal conectado via QR Code.
              </p>
            ) : null}
          </div>

          {channelId ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>
                  Grupos ({Object.keys(selected).length} selecionados)
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={syncGroups.isPending}
                  onClick={() => void onSyncGroups()}
                >
                  {syncGroups.isPending ? (
                    <Spinner />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Atualizar grupos
                </Button>
              </div>

              <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  value={groupSearch}
                  onChange={(e) => setGroupSearch(e.target.value)}
                  placeholder="Buscar grupo pelo nome…"
                  className="pl-9"
                />
              </div>

              {groupsQuery.isLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Spinner />
                  Carregando grupos…
                </div>
              ) : groups.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  Nenhum grupo salvo. Clique em “Atualizar grupos” para
                  sincronizar com o WhatsApp.
                </p>
              ) : (
                <div className="max-h-48 overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableHead className="w-10" />
                        <TableHead>Nome</TableHead>
                        <TableHead className="w-28 text-right">
                          Participantes
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredGroups.map((group) => (
                        <TableRow
                          key={group.jid}
                          className="hover:bg-muted/30 cursor-pointer"
                          onClick={() => toggleGroup(group.jid, group.subject)}
                        >
                          <TableCell className="py-2">
                            <Checkbox
                              checked={Boolean(selected[group.jid])}
                              onCheckedChange={() =>
                                toggleGroup(group.jid, group.subject)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className="py-2 text-sm">
                            <span className="line-clamp-1">
                              {group.subject}
                            </span>
                          </TableCell>
                          <TableCell className="text-muted-foreground py-2 text-right text-sm">
                            {group.size ?? '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredGroups.length === 0 ? (
                        <TableRow className="hover:bg-transparent">
                          <TableCell
                            colSpan={3}
                            className="text-muted-foreground h-20 text-center text-sm"
                          >
                            Nenhum grupo corresponde à busca.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>Mensagem</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Texto da mensagem…"
            />
            {body.trim() || media ? (
              <WhatsAppPreview
                text={body}
                media={
                  media
                    ? {
                        url: media.mediaUrl,
                        mimeType: media.mediaMimeType,
                        fileName: media.mediaFileName,
                      }
                    : null
                }
              />
            ) : null}
          </div>

          <div className="space-y-2">
            <Label>Mídia (opcional)</Label>
            {media ? (
              <div className="flex items-center gap-2 text-sm">
                <Paperclip className="h-4 w-4" />
                <span className="truncate">
                  {media.mediaFileName || 'Mídia'}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setMedia(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <MediaPickerDialog
                  orgId={orgId}
                  title="Selecionar mídia para grupos"
                  purpose="group-media"
                  accept={MEDIA_ACCEPT}
                  onSelect={selectMediaAsset}
                >
                  <Button type="button" variant="outline">
                    <Paperclip className="h-4 w-4" />
                    Selecionar mídia
                  </Button>
                </MediaPickerDialog>
                <p className="text-muted-foreground text-xs">
                  Imagem, vídeo, áudio ou documento (PDF/Office), até 25MB.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Data e hora do envio</Label>
            <Input
              type="datetime-local"
              min={toLocalInputValue(new Date(Date.now() + 60_000))}
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={() => void submit()}>
            {saving ? <Spinner /> : null}
            {editing ? 'Salvar' : 'Agendar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function MessageViewDialog({
  message,
  channelName,
  onClose,
}: {
  message: ScheduledGroupMessage | null
  channelName: string | undefined
  onClose: () => void
}) {
  return (
    <Dialog open={!!message} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-x-hidden overflow-y-auto sm:max-w-2xl">
        {message ? (
          <>
            <DialogHeader>
              <DialogTitle>Detalhes da mensagem</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{channelName ?? 'Canal'}</Badge>
                <Badge
                  variant="outline"
                  className={STATUS[message.status].className}
                >
                  {STATUS[message.status].label}
                </Badge>
              </div>

              <div className="text-muted-foreground text-sm">
                <p>Agendada para: {formatDateTime(message.scheduledAt)}</p>
                {message.sentAt ? (
                  <p>Enviada em: {formatDateTime(message.sentAt)}</p>
                ) : null}
              </div>

              {message.body || message.mediaUrl ? (
                <div className="space-y-2">
                  <Label>Conteúdo</Label>
                  <WhatsAppPreview
                    text={message.body ?? ''}
                    media={
                      message.mediaUrl
                        ? {
                            url: message.mediaUrl,
                            mimeType: message.mediaMimeType ?? '',
                            fileName: message.mediaFileName ?? '',
                          }
                        : null
                    }
                  />
                </div>
              ) : null}

              <div className="space-y-2">
                <Label>Grupos ({message.targets.length})</Label>
                <div className="max-h-56 overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableHead>Grupo</TableHead>
                        <TableHead className="w-28 text-right">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {message.targets.map((target) => {
                        const targetStatus = TARGET_STATUS[target.status]
                        return (
                          <TableRow
                            key={target.id}
                            className="hover:bg-muted/30"
                          >
                            <TableCell className="py-2 text-sm">
                              <span className="line-clamp-1">
                                {target.groupSubject ?? target.groupJid}
                              </span>
                              {target.error ? (
                                <span className="text-destructive line-clamp-2 text-xs">
                                  {target.error}
                                </span>
                              ) : null}
                            </TableCell>
                            <TableCell className="py-2 text-right">
                              <Badge
                                variant="outline"
                                className={targetStatus.className}
                              >
                                {targetStatus.label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Fechar
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
